import {
  CalendarEvent,
  CalendarEventCreate,
  CalendarEventUpdate,
  PaginatedResponse,
} from '../types';
import { ApiClient } from '../../../utils/apiClient';
import { handleApiResult } from '../../../utils/apiErrorHandler';

export const calendarEventService = {
  // List all events
  list: async (params?: {
    calendar?: string;
    provider?: string;
    status?: string;
    is_upcoming?: boolean;
    is_past?: boolean;
    start_date_after?: string;
    start_date_before?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<CalendarEvent>> => {
    const queryParams = new URLSearchParams();
    if (params?.calendar) queryParams.append('calendar', params.calendar);
    if (params?.provider) queryParams.append('provider', params.provider);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.is_upcoming !== undefined)
      queryParams.append('is_upcoming', params.is_upcoming.toString());
    if (params?.is_past !== undefined) queryParams.append('is_past', params.is_past.toString());
    if (params?.start_date_after) queryParams.append('start_date_after', params.start_date_after);
    if (params?.start_date_before)
      queryParams.append('start_date_before', params.start_date_before);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());

    const endpoint = `/calendars/calendar-events/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await ApiClient.get(endpoint);
    const result = await handleApiResult<PaginatedResponse<CalendarEvent>>(
      response,
      'Failed to fetch calendar events'
    );
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to fetch calendar events');
    }
    return result.data;
  },

  // Get single event
  get: async (id: string): Promise<CalendarEvent> => {
    const response = await ApiClient.get(`/calendars/calendar-events/${id}/`);
    const result = await handleApiResult<CalendarEvent>(response, 'Failed to fetch calendar event');
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to fetch calendar event');
    }
    return result.data;
  },

  // Create event
  create: async (data: CalendarEventCreate): Promise<CalendarEvent> => {
    const response = await ApiClient.post('/calendars/calendar-events/', data);
    const result = await handleApiResult<CalendarEvent>(
      response,
      'Failed to create calendar event'
    );
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to create calendar event');
    }
    return result.data;
  },

  // Update event
  update: async (id: string, data: CalendarEventUpdate): Promise<CalendarEvent> => {
    const response = await ApiClient.patch(`/calendars/calendar-events/${id}/`, data);
    const result = await handleApiResult<CalendarEvent>(
      response,
      'Failed to update calendar event'
    );
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to update calendar event');
    }
    return result.data;
  },

  // Delete event
  delete: async (id: string): Promise<void> => {
    const response = await ApiClient.delete(`/calendars/calendar-events/${id}/`);
    const result = await handleApiResult<void>(response, 'Failed to delete calendar event');
    if (!result.success) {
      throw new Error(result.errors?.generalError || 'Failed to delete calendar event');
    }
  },

  // Get upcoming events
  upcoming: async (): Promise<PaginatedResponse<CalendarEvent>> => {
    const response = await ApiClient.get('/calendars/calendar-events/upcoming/');
    const result = await handleApiResult<PaginatedResponse<CalendarEvent>>(
      response,
      'Failed to fetch upcoming events'
    );
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to fetch upcoming events');
    }
    return result.data;
  },

  // Get past events
  past: async (): Promise<PaginatedResponse<CalendarEvent>> => {
    const response = await ApiClient.get('/calendars/calendar-events/past/');
    const result = await handleApiResult<PaginatedResponse<CalendarEvent>>(
      response,
      'Failed to fetch past events'
    );
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to fetch past events');
    }
    return result.data;
  },

  // Get events by date range
  byDateRange: async (
    startDate: string,
    endDate: string
  ): Promise<PaginatedResponse<CalendarEvent>> => {
    const response = await ApiClient.get(
      `/calendars/calendar-events/by_date_range/?start_date=${startDate}&end_date=${endDate}`
    );
    const result = await handleApiResult<PaginatedResponse<CalendarEvent>>(
      response,
      'Failed to fetch events by date range'
    );
    if (!result.success || !result.data) {
      throw new Error(result.errors?.generalError || 'Failed to fetch events by date range');
    }
    return result.data;
  },
};
